(() => {
  'use strict';

  const ROOT_SELECTOR = '.boost-sd__product-filter-fallback';
  const SUMMARY_ID = 'b2-active-filter-summary';
  const MOBILE_QUERY = '(max-width: 798px)';
  const TECHNICAL_KEYS = new Set([
    'sort_by','sort','page','view','q','type','constraint',
    'preview_theme_id','section_id','variant','currency','country',
    '_pos','_sid','_ss','gclid','fbclid'
  ]);

  let timer = 0;

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  const isTechnicalKey = (key) => {
    if (!key) return true;
    const lower = key.toLowerCase();
    return TECHNICAL_KEYS.has(lower) || lower.startsWith('utm_');
  };

  const splitEntry = (raw) => {
    if (raw.includes('||')) return { separator: '||', values: raw.split('||').filter(Boolean) };
    if (raw.includes(',')) return { separator: ',', values: raw.split(',').filter(Boolean) };
    return { separator: null, values: [raw] };
  };

  const decode = (raw) => {
    let value = String(raw ?? '').replace(/\+/g, ' ').trim();
    try { value = decodeURIComponent(value); } catch (_) {}
    return value.replace(/_/g, ' ').trim();
  };

  const collectFilters = () => {
    const url = new URL(window.location.href);
    const filters = [];

    for (const [key, rawEntry] of url.searchParams.entries()) {
      if (isTechnicalKey(key)) continue;

      const { values } = splitEntry(rawEntry);
      values.forEach((rawValue) => {
        const label = decode(rawValue);
        if (!label) return;
        filters.push({ key, rawEntry, rawValue, label });
      });
    }

    return filters;
  };

  const updateParamAfterRemoval = (url, item) => {
    const entries = url.searchParams.getAll(item.key);
    url.searchParams.delete(item.key);
    let removed = false;

    entries.forEach((entry) => {
      const parsed = splitEntry(entry);

      if (parsed.separator) {
        const next = [...parsed.values];
        const index = removed ? -1 : next.indexOf(item.rawValue);
        if (index >= 0) {
          next.splice(index, 1);
          removed = true;
        }
        if (next.length) url.searchParams.append(item.key, next.join(parsed.separator));
        return;
      }

      if (!removed && entry === item.rawEntry) {
        removed = true;
        return;
      }

      url.searchParams.append(item.key, entry);
    });
  };

  const removeFilter = (item) => {
    const url = new URL(window.location.href);
    updateParamAfterRemoval(url, item);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const clearFilters = () => {
    const url = new URL(window.location.href);
    Array.from(new Set(Array.from(url.searchParams.keys()))).forEach((key) => {
      if (!isTechnicalKey(key)) url.searchParams.delete(key);
    });
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const buildCollectionDestination = (destination) => {
    const sourceUrl = new URL(window.location.href);
    const targetUrl = new URL(destination, window.location.origin);

    for (const [key, value] of sourceUrl.searchParams.entries()) {
      if (isTechnicalKey(key)) continue;
      targetUrl.searchParams.append(key, value);
    }

    const previewThemeId = sourceUrl.searchParams.get('preview_theme_id');
    if (previewThemeId) targetUrl.searchParams.set('preview_theme_id', previewThemeId);

    return targetUrl;
  };

  const findCountRow = () => {
    const count = document.querySelector('.boost-sd__product-count');
    if (!count) return null;

    let row = count.parentElement || count;
    for (let i = 0; i < 3 && row.parentElement; i += 1) {
      const parent = row.parentElement;
      const rect = parent.getBoundingClientRect();
      if (rect.width >= window.innerWidth * 0.72 && rect.height > 30 && rect.height < 180) {
        row = parent;
      } else {
        break;
      }
    }
    return row;
  };

  const renderFilterSummary = () => {
    const existing = document.getElementById(SUMMARY_ID);

    if (!isMobile()) {
      existing?.remove();
      return;
    }

    const filters = collectFilters();
    if (!filters.length) {
      existing?.remove();
      return;
    }

    const anchor = findCountRow();
    if (!anchor || !anchor.isConnected) return;

    const signature = filters.map((item) => item.key + '=' + item.rawValue).join('|');
    if (existing?.dataset.signature === signature) return;

    const summary = document.createElement('section');
    summary.id = SUMMARY_ID;
    summary.className = 'b2-active-filter-summary';
    summary.dataset.signature = signature;
    summary.setAttribute('aria-label', 'Filtri attivi');

    const label = document.createElement('span');
    label.className = 'b2-active-filter-summary__label';
    label.textContent = 'Filtri:';
    summary.appendChild(label);

    const chipList = document.createElement('div');
    chipList.className = 'b2-active-filter-summary__chips';

    filters.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'b2-active-filter-chip';
      button.textContent = item.label + ' ×';
      button.setAttribute('aria-label', 'Rimuovi filtro ' + item.label);
      button.addEventListener('click', () => removeFilter(item));
      chipList.appendChild(button);
    });

    summary.appendChild(chipList);

    if (filters.length > 1) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'b2-active-filter-summary__clear';
      clear.textContent = 'Rimuovi tutti';
      clear.addEventListener('click', clearFilters);
      summary.appendChild(clear);
    }

    existing?.remove();
    anchor.insertAdjacentElement('beforebegin', summary);
  };

  const localizeBoostUi = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    const lang = (document.documentElement.lang || '').toLowerCase();
    if (!root || !lang.startsWith('it')) return;

    const productCount = root.querySelector('.boost-sd__product-count');
    if (productCount) {
      const raw = productCount.textContent || '';
      const text = raw.trim();
      const rangeMatch = text.match(/^Showing\s+(\d+)\s*-\s*(\d+)\s+of\s+(\d+)\s+products$/i);
      const totalMatch = text.match(/^(\d[\d.,]*)\s+products$/i);

      if (rangeMatch) {
        productCount.textContent =
          `Visualizzati ${rangeMatch[1]}–${rangeMatch[2]} di ${rangeMatch[3]} prodotti`;
      } else if (totalMatch) {
        productCount.textContent = `${totalMatch[1]} prodotti`;
      }
    }

    const loadMore = Array.from(
      root.querySelectorAll('button, a, [role="button"]')
    ).find((element) => /^load more$/i.test((element.textContent || '').trim()));

    if (loadMore) loadMore.textContent = 'Vedi più prodotti';
  };

  const render = () => {
    renderFilterSummary();
    localizeBoostUi();
  };

  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(render, 80);
  };

  /* COLLECTION-GENDER-NAV-01
     Boost's Collection filter is navigation, not a faceted refinement.
     Intercept only the gender-family values on dedicated SEO landing families
     and perform a normal browser navigation so Shopify renders the full
     authoritative document (H1, intro, title, meta description, canonical).
     Event-driven only: one delegated click listener, no polling/interval. */
  const COLLECTION_GENDER_FAMILIES = [
    {
      paths: new Set([
        '/collections/orologi',
        '/collections/orologi-uomo',
        '/collections/orologi-donna',
        '/collections/orologi-da-bambini'
      ]),
      routes: new Map([
        ['tutti', '/collections/orologi'],
        ['uomo', '/collections/orologi-uomo'],
        ['donna', '/collections/orologi-donna'],
        ['bambini', '/collections/orologi-da-bambini']
      ])
    },
    {
      paths: new Set([
        '/collections/gioielli',
        '/collections/gioielli-uomo',
        '/collections/gioielli-donna',
        '/collections/gioielli-da-bambini'
      ]),
      routes: new Map([
        ['tutti', '/collections/gioielli'],
        ['uomo', '/collections/gioielli-uomo'],
        ['donna', '/collections/gioielli-donna'],
        ['bambini', '/collections/gioielli-da-bambini']
      ])
    }
  ];

  const normalizeBoostValueLabel = (button) => {
    const labelNode = button.querySelector('.boost-sd__filter-option-item-label');
    const raw = (labelNode?.textContent || button.textContent || '').trim();

    return raw
      .replace(/\s*\(\s*\d[\d.,]*\s*\)\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  };

  const navigateGenderCollection = (event) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (typeof event.button === 'number' && event.button > 0)
    ) return;

    const currentPath = window.location.pathname.replace(/\/$/, '');
    const family = COLLECTION_GENDER_FAMILIES.find((item) => item.paths.has(currentPath));
    if (!family) return;

    const origin = event.target;
    if (!(origin instanceof Element)) return;

    let destination = null;

    const button = origin.closest('.boost-sd__filter-option-item-button');
    if (button) {
      const label = normalizeBoostValueLabel(button);
      destination = family.routes.get(label) || null;
    }

    if (!destination) {
      const shortcut = origin.closest('.collection-shortcuts__link');
      if (shortcut) {
        try {
          const shortcutUrl = new URL(shortcut.href, window.location.origin);
          const shortcutPath = shortcutUrl.pathname.replace(/\/$/, '');
          if (
            shortcutUrl.origin === window.location.origin &&
            family.paths.has(shortcutPath)
          ) {
            destination = shortcutPath;
          }
        } catch (_) {}
      }
    }

    if (!destination) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (destination === currentPath) return;

    const targetUrl = buildCollectionDestination(destination);
    window.location.assign(targetUrl.toString());
  };

  if (!window.__b2GenderCollectionNavBound) {
    window.__b2GenderCollectionNavBound = true;
    window.addEventListener('click', navigateGenderCollection, true);
  }

  ['pushState', 'replaceState'].forEach((method) => {
    const original = history[method];
    if (typeof original !== 'function' || original.__b2CollectionWrapped) return;

    const wrapped = function (...args) {
      const manager = window.B2CollectionBrowseHistory;
      let callArgs = args;

      if (manager?.restoring) {
        const marker = manager.currentRestoreKey;
        if (marker) {
          const nextState = Object.assign({}, callArgs[0] || {});
          nextState.b2CollectionRestoreKey = marker;
          callArgs = [nextState, callArgs[1], callArgs[2]];
        }

        /* During automatic cumulative restoration, never add intermediate
           page=2/page=3 entries to browser history. Boost may advance its URL,
           but that progression belongs to the same restored collection entry. */
        if (method === 'pushState') {
          const result = history.replaceState.apply(history, callArgs);
          schedule();
          return result;
        }
      }

      const result = original.apply(this, callArgs);
      schedule();
      return result;
    };
    wrapped.__b2CollectionWrapped = true;
    history[method] = wrapped;
  });

  window.addEventListener('popstate', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('resize', schedule);


  /* COLLECTION-HISTORY-01 v3
     Preserve cumulative Boost "Load more" browsing context when a shopper
     opens a PDP and returns with browser Back. The collection history entry
     is normalized before navigation, and restoration can recover from
     sessionStorage even if another library replaces history.state. */
  const browseHistory = window.B2CollectionBrowseHistory =
    window.B2CollectionBrowseHistory || {};

  const RESTORE_STATE_KEY = 'b2CollectionRestoreKey';
  const RESTORE_STORAGE_PREFIX = 'b2CollectionBrowse:v2:';
  const RESTORE_TTL = 30 * 60 * 1000;

  const normalizedCollectionLocation = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = '';
    return url.pathname + (url.search ? url.search : '');
  };

  const browseStorageKey = (locationKey = normalizedCollectionLocation()) =>
    RESTORE_STORAGE_PREFIX + locationKey;

  const countLoadedProducts = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return 0;

    const products = new Set();
    root.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin === window.location.origin && url.pathname.includes('/products/')) {
          products.add(url.pathname);
        }
      } catch (_) {}
    });
    return products.size;
  };

  const findLoadMoreControl = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return null;

    return Array.from(root.querySelectorAll('button, a, [role="button"]')).find((element) => {
      const label = (element.textContent || '').trim();
      if (!/^(Vedi più prodotti|Load more)$/i.test(label)) return false;
      if (element.disabled || element.getAttribute('aria-disabled') === 'true') return false;
      return element.getClientRects().length > 0;
    }) || null;
  };

  const navigationIsBackForward = () => {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      return entry && entry.type === 'back_forward';
    } catch (_) {
      return false;
    }
  };

  const readPendingPayload = () => {
    const state = history.state || {};
    const locationKey = normalizedCollectionLocation();
    const stateKey = state[RESTORE_STATE_KEY];
    const fallbackKey = browseStorageKey(locationKey);
    const candidates = Array.from(new Set([stateKey, fallbackKey].filter(Boolean)));

    for (const key of candidates) {
      try {
        const payload = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (
          payload &&
          payload.pendingReturn === true &&
          payload.locationKey === locationKey &&
          Date.now() - payload.savedAt <= RESTORE_TTL
        ) {
          return { key, payload };
        }
      } catch (_) {}
    }

    return null;
  };

  const saveBrowseStateBeforeProduct = (link) => {
    const locationKey = normalizedCollectionLocation();
    const key = browseStorageKey(locationKey);
    const loadedCount = countLoadedProducts();

    if (!loadedCount) return;

    let productPath = '';
    try {
      const productUrl = new URL(link.href, window.location.origin);
      if (
        productUrl.origin !== window.location.origin ||
        !productUrl.pathname.includes('/products/')
      ) return;
      productPath = productUrl.pathname;
    } catch (_) {
      return;
    }

    const payload = {
      locationKey,
      loadedCount,
      scrollY: window.scrollY || 0,
      productPath,
      pendingReturn: true,
      savedAt: Date.now()
    };

    try {
      window.sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (_) {
      return;
    }

    /* This runs from WINDOW capture before Turbo/InstantClick document handlers.
       The collection history entry must point to the cumulative collection,
       not to Boost's transient page=3/page=4 segment. */
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const nextState = Object.assign({}, history.state || {});
    nextState[RESTORE_STATE_KEY] = key;

    history.replaceState(
      nextState,
      '',
      url.pathname + (url.search ? url.search : '') + (url.hash || '')
    );

    browseHistory.pendingKey = key;
  };

  const waitForProductCountIncrease = (previousCount, timeout = 8000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        const currentCount = countLoadedProducts();
        if (currentCount > previousCount || Date.now() - startedAt >= timeout) {
          resolve(currentCount);
          return;
        }
        window.setTimeout(check, 120);
      };

      check();
    });

  const waitForCollectionRoot = (timeout = 8000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        const root = document.querySelector(ROOT_SELECTOR);
        if (root || Date.now() - startedAt >= timeout) {
          resolve(root || null);
          return;
        }
        window.setTimeout(check, 100);
      };

      check();
    });

  const restoreBrowseState = async (returnSignal = false) => {
    if (browseHistory.restoring) return;

    const pending = readPendingPayload();
    if (!pending) return;

    const { key, payload } = pending;
    const currentUrl = new URL(window.location.href);
    const hasTransientPage = currentUrl.searchParams.has('page');

    const shouldRestore =
      payload.forceRestore === true ||
      hasTransientPage ||
      returnSignal ||
      browseHistory.backSignal === true ||
      navigationIsBackForward();

    if (!shouldRestore) return;
    browseHistory.backSignal = false;
    browseHistory.restoring = true;
    browseHistory.currentRestoreKey = key;

    try {
      const root = await waitForCollectionRoot();
      if (!root) return;

      let loadedCount = countLoadedProducts();

      /* Mobile browsers may restore Boost directly at page=N and render only
         that isolated segment (for example 49–72). In that state Load More
         would continue from page N instead of rebuilding 1–72. Canonicalize
         once to page 1, preserve the pending payload, and force restoration
         on the canonical reload. Desktop bfcache can already contain the full
         cumulative DOM; in that case no reload is needed. */
      if (
        hasTransientPage &&
        loadedCount < payload.loadedCount &&
        payload.forceRestore !== true
      ) {
        payload.forceRestore = true;
        payload.savedAt = Date.now();

        try {
          window.sessionStorage.setItem(key, JSON.stringify(payload));
        } catch (_) {}

        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('page');

        const nextState = Object.assign({}, history.state || {});
        nextState[RESTORE_STATE_KEY] = key;
        history.replaceState(
          nextState,
          '',
          cleanUrl.pathname + (cleanUrl.search ? cleanUrl.search : '') + (cleanUrl.hash || '')
        );

        window.location.replace(
          cleanUrl.pathname + (cleanUrl.search ? cleanUrl.search : '') + (cleanUrl.hash || '')
        );
        return;
      }

      let attempts = 0;
      const maxAttempts = Math.max(4, Math.ceil(payload.loadedCount / 24) + 3);

      while (loadedCount < payload.loadedCount && attempts < maxAttempts) {
        const control = findLoadMoreControl();

        if (!control) {
          await new Promise((resolve) => window.setTimeout(resolve, 300));
          attempts += 1;
          continue;
        }

        const before = loadedCount;
        control.click();
        loadedCount = await waitForProductCountIncrease(before);
        attempts += 1;

        if (loadedCount <= before) break;
      }

      const finalUrl = new URL(window.location.href);
      finalUrl.searchParams.delete('page');

      const cleanState = Object.assign({}, history.state || {});
      delete cleanState[RESTORE_STATE_KEY];

      browseHistory.currentRestoreKey = null;
      history.replaceState(
        cleanState,
        '',
        finalUrl.pathname + (finalUrl.search ? finalUrl.search : '') + (finalUrl.hash || '')
      );

      window.setTimeout(() => {
        if (Number.isFinite(payload.scrollY) && payload.scrollY > 0) {
          window.scrollTo({ top: payload.scrollY, left: 0, behavior: 'auto' });
        } else if (payload.productPath) {
          const target = Array.from(
            document.querySelectorAll(ROOT_SELECTOR + ' a[href*="/products/"]')
          ).find((candidate) => {
            try {
              return new URL(candidate.href, window.location.origin).pathname === payload.productPath;
            } catch (_) {
              return false;
            }
          });
          target?.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }, 120);

      payload.forceRestore = false;
      try { window.sessionStorage.removeItem(key); } catch (_) {}
      browseHistory.pendingKey = null;
    } finally {
      browseHistory.currentRestoreKey = null;
      browseHistory.restoring = false;
    }
  };

  const captureProductNavigation = (event) => {
    if (
      event.defaultPrevented ||
      event.button > 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) return;

    const target = event.target;
    const link = target && target.closest && target.closest('a[href*="/products/"]');
    if (!link || link.target === '_blank') return;

    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    /* Do not depend on document-level ordering: window capture runs before
       InstantClick/Boost document handlers on desktop and mobile. */
    saveBrowseStateBeforeProduct(link);
  };

  if (!browseHistory.productClickBoundV2) {
    browseHistory.productClickBoundV2 = true;
    window.addEventListener('click', captureProductNavigation, true);
  }

  window.addEventListener('pageshow', (event) => {
    window.setTimeout(
      () => restoreBrowseState(event.persisted || navigationIsBackForward()),
      120
    );
  });

  window.addEventListener('popstate', () => {
    browseHistory.backSignal = true;
    window.setTimeout(() => restoreBrowseState(true), 120);
  });

  if (
    window.InstantClick &&
    typeof window.InstantClick.on === 'function' &&
    !browseHistory.instantClickBoundV2
  ) {
    browseHistory.instantClickBoundV2 = true;
    window.InstantClick.on('change', () => {
      window.setTimeout(
        () => restoreBrowseState(browseHistory.backSignal === true),
        180
      );
    });
  }

  const start = () => {
    const observer = new MutationObserver(schedule);
    const observerRoot = document.querySelector(ROOT_SELECTOR) || document.body;
    observer.observe(observerRoot, { childList: true, subtree: true });
    render();
    window.setTimeout(render, 400);
    window.setTimeout(render, 1200);
    window.setTimeout(
      () => restoreBrowseState(navigationIsBackForward()),
      250
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
